import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContactRoundIcon, EllipsisIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import { BackLink, Button, ConfirmDialog, DataTable, EmptyState, ErrorNotice, FactList, Field, ListField, LoadingState, PageEyebrow, PageHeading, Pagination, Panel, Pill, SearchInput, SelectInput, SkeletonTable, TextArea, TextInput, type DataTableSort } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { readMyIdentity } from '@/lib/events';
import {
  ASSISTANT_ROLE_LABELS,
  STAFF_SOURCES,
  STAFF_SOURCE_LABELS,
  assistantStatusTone,
  createStaffContact,
  deleteStaffContact,
  listStaffContactEvents,
  listStaffContacts,
  readStaffContact,
  updateStaffContact,
  type StaffContact,
  type StaffContactEvent,
  type StaffContactWrite,
  type StaffSource,
} from '@/lib/staffing';

const SORT_FIELDS = { name: 'name', where: 'whereText', source: 'sourceLabel', skills: 'skillsText' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.name, direction: 'asc' };
const CONTACTS_PAGE_SIZE = 25;

/** An event a staff contact worked, with the text its row shows, for the search. */
interface WorkedEventRow extends StaffContactEvent {
  readonly place: string;
  readonly roleLabel: string;
}

/** The fields the events-worked search matches. */
const WORKED_EVENT_SEARCH_FIELDS: ReadonlyArray<keyof WorkedEventRow> = ['contractNumber', 'opportunityName', 'programName', 'place', 'roleLabel', 'status'];

interface ContactRow extends StaffContact {
  readonly whereText: string;
  readonly sourceLabel: string;
  readonly skillsText: string;
}
function contactRow(contact: StaffContact): ContactRow {
  return { ...contact, whereText: [contact.city, contact.state].filter(Boolean).join(', '), sourceLabel: STAFF_SOURCE_LABELS[contact.source] ?? contact.source, skillsText: contact.skills.join(', ') };
}

/** People → Staff contacts: assistants, techs and other event workers without a login. */
export function StaffContactsPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<StaffContact | null>(null);
  const { search, setSearch } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  const isInternal = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');
  const contacts = useQuery({ queryKey: ['staff-contacts'], queryFn: ({ signal }) => listStaffContacts(undefined, signal) });
  const remove = useMutation({
    mutationFn: (contact: StaffContact) => deleteStaffContact(contact._id),
    onSuccess: async (_result, contact) => {
      toast.success(`Removed ${contact.name}`);
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: ['staff-contacts'] });
    },
  });
  const rows = contacts.data ? sortRows(filterRows(contacts.data.map(contactRow), search, ['name', 'whereText', 'skillsText', 'email']), sort) : [];
  const totalPages = Math.max(1, Math.ceil(rows.length / CONTACTS_PAGE_SIZE));
  const visible = rows.slice((page - 1) * CONTACTS_PAGE_SIZE, page * CONTACTS_PAGE_SIZE);

  return (
    <section className="animate-portal-rise">
      <PageHeading eyebrow="PEOPLE" title="Staff contacts" subtitle="Assistants, guitar techs and other event workers who do not log in. One record each, so nobody is entered twice." />
      <div className="mt-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            data-guide="staff-search"
            label="Search staff contacts"
            placeholder="Name, city, skill or email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="w-full max-w-sm"
          />
          {isInternal ? (
            <span data-guide="staff-new" className="ml-auto">
              <Button to="/staff-contacts/new">
                <PlusIcon aria-hidden className="size-4" /> New contact
              </Button>
            </span>
          ) : null}
        </div>
        {contacts.isPending ? (
          <SkeletonTable label="Loading staff contacts…" />
        ) : contacts.isError ? (
          <ErrorNotice title="Staff contacts unavailable" message={errorMessage(contacts.error)} />
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState icon={<ContactRoundIcon aria-hidden className="size-6" />} title={contacts.data.length === 0 ? 'No staff contacts yet' : 'No one matches'} message={contacts.data.length === 0 ? 'They appear here as assistants are added to events, or add one now.' : 'Try another name, city or skill.'} {...(contacts.data.length === 0 ? { action: <Button to="/staff-contacts/new">New contact</Button> } : {})} />
          </Panel>
        ) : (
          <div data-guide="staff-table"><DataTable headers={[{ label: 'Name', sortKey: SORT_FIELDS.name }, { label: 'Where', sortKey: SORT_FIELDS.where }, { label: 'Skills', sortKey: SORT_FIELDS.skills }, { label: 'Source', sortKey: SORT_FIELDS.source }, 'Status', 'Actions']} visuallyHiddenHeaders={[5]} sort={sort} onSort={onSort} minWidth={800}>
            {visible.map((contact) => (
              <tr key={contact._id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <Link to={`/staff-contacts/${contact._id}`} className="block font-bold text-brand hover:underline">{contact.name}</Link>
                  <span className="block text-portal-micro text-portal-muted">{[contact.phone, contact.email].filter(Boolean).join(' · ')}</span>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{contact.whereText || '—'}</td>
                <td className="p-3 text-portal-small text-portal-muted"><span className="block max-w-[260px] truncate">{contact.skillsText || '—'}</span></td>
                <td className="p-3 text-portal-small text-portal-muted">{contact.sourceLabel}</td>
                <td className="p-3 text-portal-small"><Pill tone={contact.isActive ? 'success' : 'neutral'}>{contact.isActive ? 'Active' : 'Inactive'}</Pill></td>
                <td className="p-3 text-right">
                  {isInternal ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" data-guide="staff-row-menu" aria-label={`Actions for ${contact.name}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"><EllipsisIcon aria-hidden className="size-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => navigate(`/staff-contacts/${contact._id}/edit`)}><PencilIcon aria-hidden className="size-4" /> Edit</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(contact)}><XIcon aria-hidden className="size-4" /> Remove</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </td>
              </tr>
            ))}
          </DataTable></div>
        )}
        {rows.length > 0 ? <Pagination page={page} totalPages={totalPages} onChange={setPage} summary={`${rows.length} ${rows.length === 1 ? 'person' : 'people'}`} /> : null}
      </div>
      <ConfirmDialog open={removing !== null} tone="danger" title={`Remove ${removing?.name ?? 'this contact'}?`} message="They leave the directory; events they worked keep the record." confirmLabel="Remove" pending={remove.isPending} error={remove.isError ? errorMessage(remove.error) : undefined} onConfirm={() => { if (removing) remove.mutate(removing); }} onCancel={() => { if (!remove.isPending) setRemoving(null); }} />
    </section>
  );
}

/** One staff contact: details and the events they worked. */
export function StaffContactPage(): React.ReactElement {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  const isInternal = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');
  const contact = useQuery({ queryKey: ['staff-contact', contactId], queryFn: ({ signal }) => readStaffContact(contactId ?? '', signal), enabled: contactId !== undefined });
  const events = useQuery({ queryKey: ['staff-contact-events', contactId], queryFn: ({ signal }) => listStaffContactEvents(contactId ?? '', signal), enabled: contactId !== undefined });
  const { search, setSearch } = useUrlSearch();
  const eventRows = filterRows<WorkedEventRow>(
    (events.data ?? []).map((event) => ({ ...event, place: [event.city, event.state].filter(Boolean).join(', '), roleLabel: event.role ? ASSISTANT_ROLE_LABELS[event.role] : '' })),
    search,
    WORKED_EVENT_SEARCH_FIELDS,
  );
  if (contact.isPending) return <LoadingState label="Loading contact…" />;
  if (contact.isError) return <ErrorNotice title="Contact unavailable" message={errorMessage(contact.error)} />;
  const person = contact.data;
  return (
    <section className="animate-portal-rise">
      <BackLink to="/staff-contacts">All staff contacts</BackLink>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <PageEyebrow>STAFF CONTACT</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1.7px]">{person.name}</h1>
        </div>
        {isInternal ? <Button variant="secondary" onClick={() => navigate(`/staff-contacts/${person._id}/edit`)}><PencilIcon aria-hidden className="size-4" /> Edit</Button> : null}
      </div>
      <Panel guide="staff-facts" className="mt-4 p-5">
        <FactList columns={3} showEmpty facts={[{ label: 'Phone', value: person.phone }, { label: 'Email', value: person.email }, { label: 'Based in', value: [person.city, person.state].filter(Boolean).join(', ') }, { label: 'Skills', value: person.skills.join(', ') }, { label: 'Source', value: STAFF_SOURCE_LABELS[person.source] }, { label: 'Status', value: person.isActive ? 'Active' : 'Inactive' }, { label: 'Notes', value: person.notes }]} />
      </Panel>
      <h2 data-guide="staff-events" className="mt-6 mb-2 text-portal-card-title font-bold">Events worked</h2>
      {(events.data ?? []).length > 0 ? (
        <SearchInput
          label="Search events worked"
          placeholder="Contract, event, program, place, role or status"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="mb-3 w-full max-w-sm"
        />
      ) : null}
      {events.isPending ? (
        <LoadingState label="Loading events…" />
      ) : events.isError ? (
        <ErrorNotice title="Events unavailable" message={errorMessage(events.error)} />
      ) : events.data.length === 0 ? (
        <Panel><EmptyState icon={<ContactRoundIcon aria-hidden className="size-6" />} title="No events yet" message="Events list here once they are added as an assistant." /></Panel>
      ) : eventRows.length === 0 ? (
        <Panel><EmptyState title="No events match" message="Try a different search." /></Panel>
      ) : (
        <DataTable headers={['Date', 'Event', 'Where', 'Role', 'Status']}>
          {eventRows.map((event) => (
            <tr key={event._id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small whitespace-nowrap">{event.eventDate ? new Date(event.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—'}</td>
              <td className="p-3 text-portal-small"><Link to={`/events/${event._id}?tab=people`} className="font-bold text-brand hover:underline">{[event.contractNumber, event.opportunityName].filter(Boolean).join(' · ')}</Link><span className="block text-portal-micro text-portal-muted">{event.programName}</span></td>
              <td className="p-3 text-portal-small text-portal-muted">{event.place || '—'}</td>
              <td className="p-3 text-portal-small text-portal-muted">{event.roleLabel || '—'}</td>
              <td className="p-3 text-portal-small">{event.status ? <Pill tone={assistantStatusTone(event.status)}>{event.status[0]!.toUpperCase() + event.status.slice(1)}</Pill> : '—'}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}

/** Create or edit a staff contact — a page, not a modal. */
export function StaffContactEditPage(): React.ReactElement {
  const { contactId } = useParams<{ contactId: string }>();
  const isNew = contactId === undefined || contactId === 'new';
  const contact = useQuery({ queryKey: ['staff-contact', contactId], queryFn: ({ signal }) => readStaffContact(contactId ?? '', signal), enabled: !isNew });
  if (!isNew && contact.isPending) return <LoadingState label="Loading contact…" />;
  if (!isNew && contact.isError) return <ErrorNotice title="Contact unavailable" message={errorMessage(contact.error)} />;
  return (
    <section className="animate-portal-rise">
      <BackLink to={isNew ? '/staff-contacts' : `/staff-contacts/${contactId}`}>{isNew ? 'All staff contacts' : 'Back to contact'}</BackLink>
      <header className="mt-4">
        <PageEyebrow>STAFF CONTACT</PageEyebrow>
        <h1 className="text-portal-page tracking-[-1.7px]">{contact.data?.name ?? 'New staff contact'}</h1>
      </header>
      <StaffContactForm key={contact.data?._id ?? 'new'} contact={contact.data ?? null} />
    </section>
  );
}

function StaffContactForm({ contact }: { readonly contact: StaffContact | null }): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState(contact?.name ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [city, setCity] = useState(contact?.city ?? '');
  const [state, setState] = useState(contact?.state ?? '');
  const [skills, setSkills] = useState<readonly string[]>(contact?.skills ?? []);
  const [notes, setNotes] = useState(contact?.notes ?? '');
  const [source, setSource] = useState<StaffSource>(contact?.source ?? 'facilitator');
  const [isActive, setIsActive] = useState(contact?.isActive ?? true);
  const save = useMutation({
    mutationFn: () => {
      const body: StaffContactWrite = { name: name.trim(), email: email.trim(), phone: phone.trim(), city: city.trim(), state: state.trim(), skills: [...skills], notes: notes.trim(), source, isActive };
      return contact ? updateStaffContact(contact._id, body) : createStaffContact(body);
    },
    onSuccess: async (saved) => {
      toast.success(contact ? `Saved ${saved.name}` : `Added ${saved.name}`);
      await queryClient.invalidateQueries({ queryKey: ['staff-contacts'] });
      await queryClient.invalidateQueries({ queryKey: ['staff-contact', saved._id] });
      navigate(`/staff-contacts/${saved._id}`);
    },
  });
  return (
    <form className="mt-6 space-y-6" onSubmit={(event) => { event.preventDefault(); if (name.trim() && !save.isPending) save.mutate(); }}>
      <Panel className="space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field guide="staff-name" label="Name" htmlFor="staff-name" required><TextInput id="staff-name" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} /></Field>
          <Field guide="staff-source" label="Source" htmlFor="staff-source">
            <SelectInput id="staff-source" value={source} onChange={(event) => setSource(event.target.value as StaffSource)}>
              {STAFF_SOURCES.map((key) => <option key={key} value={key}>{STAFF_SOURCE_LABELS[key]}</option>)}
            </SelectInput>
          </Field>
          <Field guide="staff-email" label="Email" htmlFor="staff-email"><TextInput id="staff-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field>
          <Field guide="staff-phone" label="Phone" htmlFor="staff-phone"><TextInput id="staff-phone" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} /></Field>
          <Field guide="staff-city" label="City" htmlFor="staff-city"><TextInput id="staff-city" value={city} onChange={(event) => setCity(event.target.value)} /></Field>
          <Field guide="staff-state" label="State" htmlFor="staff-state"><TextInput id="staff-state" value={state} maxLength={40} onChange={(event) => setState(event.target.value)} /></Field>
        </div>
        <ListField guide="staff-skills" label="Skills, one per line" value={skills} onChange={setSkills} placeholder={'Guitar tech\nStage sound\nDrives a van'} rows={4} />
        <Field guide="staff-notes" label="Notes" htmlFor="staff-notes"><TextArea id="staff-notes" rows={3} value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} /></Field>
        <label data-guide="staff-active" className="flex items-center gap-2 text-portal-small"><input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} /> Active</label>
      </Panel>
      {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
      <div className="flex items-center justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => navigate('/staff-contacts')} disabled={save.isPending}>Cancel</Button>
        <Button type="submit" disabled={!name.trim() || save.isPending}>{save.isPending ? 'Saving…' : contact ? 'Save' : 'Add contact'}</Button>
      </div>
    </form>
  );
}
